import clsx from "clsx";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import Icon, { type IconName } from "../components/Icon";
import { Counter, type Tone } from "./Badge";

/* ==========================================================================
   Вкладки
   ========================================================================== */

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
  count?: number;
  countTone?: Tone;
}

export function Tabs<T extends string>({
  value,
  onChange,
  items,
  label,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  items: TabItem<T>[];
  /** Доступное имя набора вкладок для скринридера. */
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={clsx("flex gap-1 overflow-x-auto border-b border-line scrollbar-thin", className)}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={clsx(
              "-mb-px inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              active
                ? "border-accent text-ink"
                : "border-transparent text-muted hover:border-line-strong hover:text-ink",
            )}
          >
            {item.icon && <Icon name={item.icon} size={14} />}
            {item.label}
            {item.count !== undefined && (
              <Counter value={item.count} tone={item.countTone ?? "neutral"} />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Вкладки-ссылки: состояние живёт в URL. */
export function LinkTabs({
  items,
  className,
}: {
  items: { to: string; label: ReactNode; icon?: IconName; count?: number; end?: boolean }[];
  className?: string;
}) {
  return (
    <div className={clsx("flex gap-1 overflow-x-auto border-b border-line scrollbar-thin", className)}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            clsx(
              "-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "border-accent text-ink"
                : "border-transparent text-muted hover:border-line-strong hover:text-ink",
            )
          }
        >
          {item.icon && <Icon name={item.icon} size={14} />}
          {item.label}
          {item.count !== undefined && <Counter value={item.count} />}
        </NavLink>
      ))}
    </div>
  );
}

/* ==========================================================================
   Показатель
   ========================================================================== */

export function Stat({
  label,
  value,
  hint,
  icon,
  tone = "neutral",
  trend,
  to,
  onClick,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: IconName;
  tone?: Tone;
  /** Изменение к прошлому периоду: положительное — рост. */
  trend?: { value: number; label?: string; goodWhen?: "up" | "down" };
  to?: string;
  onClick?: () => void;
  className?: string;
}) {
  const accentText: Record<Tone, string> = {
    neutral: "text-ink",
    accent: "text-accent",
    success: "text-success-ink",
    warning: "text-warning-ink",
    danger: "text-danger-ink",
    info: "text-info-ink",
  };

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-muted">{label}</span>
        {icon && <Icon name={icon} size={15} className="mt-px text-faint" />}
      </div>
      <div className={clsx("mt-2 text-2xl font-semibold tabular", accentText[tone])}>{value}</div>
      <div className="mt-1 flex items-center gap-2">
        {trend && trend.value !== 0 && <TrendMark {...trend} />}
        {hint && <span className="truncate text-xs text-faint">{hint}</span>}
      </div>
    </>
  );

  const classes = clsx(
    "rounded-lg border border-line bg-surface p-3.5 shadow-xs transition-colors",
    (to || onClick) && "cursor-pointer hover:border-line-strong hover:bg-sunken/50",
    className,
  );

  if (to) {
    return (
      <NavLink to={to} className={classes}>
        {body}
      </NavLink>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={clsx(classes, "text-left")}>
        {body}
      </button>
    );
  }
  return <div className={classes}>{body}</div>;
}

function TrendMark({
  value,
  label,
  goodWhen = "up",
}: {
  value: number;
  label?: string;
  goodWhen?: "up" | "down";
}) {
  const up = value > 0;
  const good = goodWhen === "up" ? up : !up;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular",
        good ? "text-success-ink" : "text-danger-ink",
      )}
    >
      <Icon name="trend" size={12} className={up ? "" : "-scale-y-100"} />
      {up ? "+" : ""}
      {value}%{label && <span className="ml-1 font-normal text-faint">{label}</span>}
    </span>
  );
}

/* ==========================================================================
   Пары «свойство — значение»
   ========================================================================== */

export function DescriptionList({
  items,
  columns = 1,
  className,
}: {
  items: { label: ReactNode; value: ReactNode; wide?: boolean }[];
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <dl
      className={clsx(
        "grid gap-x-6 gap-y-3",
        columns === 3 ? "sm:grid-cols-3" : columns === 2 ? "sm:grid-cols-2" : "",
        className,
      )}
    >
      {items.map((item, index) => (
        <div key={index} className={clsx("min-w-0", item.wide && "sm:col-span-full")}>
          <dt className="text-xs text-muted">{item.label}</dt>
          <dd className="mt-0.5 break-words text-sm text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ==========================================================================
   Аватар
   ========================================================================== */

/** Стабильный оттенок по имени: один и тот же человек всегда одного цвета. */
const avatarTones = [
  "bg-info-soft text-info-ink",
  "bg-success-soft text-success-ink",
  "bg-warning-soft text-warning-ink",
  "bg-accent-soft text-accent",
  "bg-danger-soft text-danger-ink",
];

export function Avatar({
  name,
  size = 28,
  className,
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997;
  return (
    <span
      title={name}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold",
        avatarTones[hash % avatarTones.length],
        className,
      )}
    >
      {initials || "?"}
    </span>
  );
}

/* ==========================================================================
   Полоса прогресса
   ========================================================================== */

export function Progress({
  value,
  tone = "accent",
  className,
  label,
}: {
  /** 0…100 */
  value: number;
  tone?: Tone;
  className?: string;
  label?: ReactNode;
}) {
  const fill: Record<Tone, string> = {
    neutral: "bg-faint",
    accent: "bg-accent",
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
    info: "bg-info",
  };
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className={className}>
      {label && <div className="mb-1 flex justify-between text-xs text-muted">{label}</div>}
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1.5 w-full overflow-hidden rounded-full bg-sunken"
      >
        <div
          className={clsx("h-full rounded-full transition-[width] duration-300", fill[tone])}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
